// Ported from ops-portal/src/app/pages/manage-businesses/single-business/
// Route: /manage-businesses/be/:id
import {
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useMemo, useState } from 'react';
import Swal from 'sweetalert2';
import { useDialog } from '@/components/dialogs/useDialog';
import { ConfirmBlockModal } from './ConfirmBlockModal';
import { useBusinessEntityQuery } from './api';
import type { BusinessEntityAddress, BusinessEntityMember } from '@/types/business-entity';
import type { M3ApiResponse } from '@/types/api';

function formatAddress(addressInfo: BusinessEntityAddress | null | undefined): string {
  if (!addressInfo) return 'None.';
  let result = '';
  if (addressInfo.address) result = addressInfo.address + ', ';
  if (addressInfo.city) result += addressInfo.city.name + ', ' + addressInfo.city.country.name;
  return result || 'None.';
}

const ROLE_KEYS: Record<string, string> = {
  primary_owner: 'pages.single_business.table.roles.primary_owner',
  owner: 'pages.single_business.table.roles.owner',
  member: 'pages.single_business.table.roles.member',
  viewer: 'pages.single_business.table.roles.viewer',
  cashier: 'pages.single_business.table.roles.cashier',
  agent: 'pages.single_business.table.roles.agent',
  sales: 'pages.single_business.table.roles.sales',
};

export function SingleBusinessPage() {
  const { t } = useTranslation();
  const { id = '' } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { open: openDialog } = useDialog();
  const query = useBusinessEntityQuery(id);
  const be = query.data;

  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const rowsPerPage = 10;

  const filteredMembers = useMemo(() => {
    const team = be?.team ?? [];
    if (!search.trim()) return team;
    const term = search.toLowerCase();
    return team.filter(
      (m) =>
        m.email.toLowerCase().includes(term) ||
        (m.firstname ?? '').toLowerCase().includes(term) ||
        (m.lastname ?? '').toLowerCase().includes(term),
    );
  }, [be?.team, search]);

  const pagedMembers = filteredMembers.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const handleToggleBe = async (block: boolean) => {
    if (!be) return;
    const result = await openDialog<M3ApiResponse | null>((close) => (
      <ConfirmBlockModal
        be={be}
        member={be.primaryOwner ?? null}
        block={block}
        isBe
        onClose={close}
      />
    ));
    if (!result) return;
    if (result.code === 0) {
      const successKey = block
        ? 'pages.single_business.description_modal.block_be.success'
        : 'pages.single_business.description_modal.restore_be.success';
      const msgKey = block
        ? 'pages.single_business.description_modal.block_be.success_message'
        : 'pages.single_business.description_modal.restore_be.success_message';
      await Swal.fire(
        t(successKey, { defaultValue: 'Success' }),
        t(msgKey, { defaultValue: '' }),
        'success',
      );
    } else {
      const errKey = block
        ? 'pages.single_business.description_modal.block_be.error'
        : 'pages.single_business.description_modal.restore_be.error';
      await Swal.fire(
        t('messages.error', { defaultValue: 'Error' }),
        t(errKey, { defaultValue: result.message }),
        'error',
      );
    }
  };

  const handleToggleUser = async (member: BusinessEntityMember, block: boolean) => {
    if (!be) return;
    const result = await openDialog<M3ApiResponse | null>((close) => (
      <ConfirmBlockModal be={be} member={member} block={block} isBe={false} onClose={close} />
    ));
    if (!result) return;
    if (result.code === 0) {
      const successKey = block
        ? 'pages.single_business.description_modal.block_user.success'
        : 'pages.single_business.description_modal.restore_user.success';
      const msgKey = block
        ? 'pages.single_business.description_modal.block_user.success_message'
        : 'pages.single_business.description_modal.restore_user.success';
      await Swal.fire(
        t(successKey, { defaultValue: 'Success' }),
        t(msgKey, { defaultValue: '' }),
        'success',
      );
    } else {
      const errKey = block
        ? 'pages.single_business.description_modal.block_user.error'
        : 'pages.single_business.description_modal.restore_user.error';
      await Swal.fire(
        t('messages.error', { defaultValue: 'Error' }),
        t(errKey, { defaultValue: result.message }),
        'error',
      );
    }
  };

  if (query.isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!be) {
    return (
      <Typography color="error">
        {t('messages.no-data', { defaultValue: 'No data' })}
      </Typography>
    );
  }

  return (
    <Box>
      <Paper sx={{ p: 3 }}>
        {/* ── Header ── */}
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="h6">{be.companyName}</Typography>
            <Chip
              size="small"
              label={t(
                `pages.manage_businesses.be_status.${be.isFreezed ? 'freezed' : 'active'}`,
                { defaultValue: be.isFreezed ? 'Freezed' : 'Active' },
              )}
              color={be.isFreezed ? 'error' : 'success'}
            />
          </Stack>
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/manage-businesses')}
          >
            {t('global.buttons.back_to_business_entities', { defaultValue: 'Back to Business Entities' })}
          </Button>
        </Stack>

        <Divider sx={{ mb: 2 }} />

        {/* ── Detail cards ── */}
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={3} sx={{ mb: 2 }}>
          {/* Business details */}
          <Box flex={1}>
            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
              {t('pages.manage_businesses.business_details', { defaultValue: 'Business Details' })}
            </Typography>
            <Stack spacing={0.5}>
              <Stack direction="row" spacing={1}>
                <Typography variant="body2" color="text.secondary" sx={{ minWidth: 120 }}>
                  {t('pages.single_business.reg_id', { defaultValue: 'Reg. ID' })}
                </Typography>
                <Typography variant="body2">{be.businessRegistration ?? '–'}</Typography>
              </Stack>
              <Stack direction="row" spacing={1}>
                <Typography variant="body2" color="text.secondary" sx={{ minWidth: 120 }}>
                  {t('pages.single_business.tax_id', { defaultValue: 'Tax ID' })}
                </Typography>
                <Typography variant="body2">{be.taxCard ?? '–'}</Typography>
              </Stack>
              <Stack direction="row" spacing={1}>
                <Typography variant="body2" color="text.secondary" sx={{ minWidth: 120 }}>
                  {t('pages.single_business.address', { defaultValue: 'Address' })}
                </Typography>
                <Typography variant="body2">{formatAddress(be.addressInfo)}</Typography>
              </Stack>
              <Stack direction="row" spacing={1}>
                <Typography variant="body2" color="text.secondary" sx={{ minWidth: 120 }}>
                  {t('pages.single_business.plus_code', { defaultValue: 'Plus Code' })}
                </Typography>
                <Typography variant="body2">{be.addressInfo?.plusCode ?? '–'}</Typography>
              </Stack>
            </Stack>
          </Box>

          {/* Contact person */}
          {be.primaryOwner && (
            <Box flex={1}>
              <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
                {t('pages.manage_businesses.contact_person', { defaultValue: 'Contact Person' })}
              </Typography>
              <Stack spacing={0.5}>
                <Stack direction="row" spacing={1}>
                  <Typography variant="body2" color="text.secondary" sx={{ minWidth: 120 }}>
                    {t('pages.single_business.first_name', { defaultValue: 'First name' })}
                  </Typography>
                  <Typography variant="body2">{be.primaryOwner.firstname}</Typography>
                </Stack>
                <Stack direction="row" spacing={1}>
                  <Typography variant="body2" color="text.secondary" sx={{ minWidth: 120 }}>
                    {t('pages.single_business.last_name', { defaultValue: 'Last name' })}
                  </Typography>
                  <Typography variant="body2">{be.primaryOwner.lastname}</Typography>
                </Stack>
                <Stack direction="row" spacing={1}>
                  <Typography variant="body2" color="text.secondary" sx={{ minWidth: 120 }}>
                    {t('pages.single_business.email', { defaultValue: 'Email' })}
                  </Typography>
                  <Typography variant="body2">{be.primaryOwner.email}</Typography>
                </Stack>
                <Stack direction="row" spacing={1}>
                  <Typography variant="body2" color="text.secondary" sx={{ minWidth: 120 }}>
                    {t('pages.single_business.mobile_no', { defaultValue: 'Mobile' })}
                  </Typography>
                  <Typography variant="body2">
                    {[be.primaryOwner.phoneCode, be.primaryOwner.phone].filter(Boolean).join(' ')}
                  </Typography>
                </Stack>
              </Stack>
            </Box>
          )}
        </Stack>

        {/* ── Block / Restore BE ── */}
        <Stack direction="row" justifyContent="flex-end" sx={{ mb: 2 }}>
          {be.isFreezed ? (
            <Button
              variant="outlined"
              color="success"
              onClick={() => { void handleToggleBe(false); }}
            >
              {t('global.buttons.restore', { defaultValue: 'Restore' })}
            </Button>
          ) : (
            <Button
              variant="contained"
              color="error"
              onClick={() => { void handleToggleBe(true); }}
            >
              {t('global.buttons.block', { defaultValue: 'Block' })}
            </Button>
          )}
        </Stack>

        <Divider sx={{ mb: 2 }} />

        {/* ── Team members table ── */}
        <Stack direction="row" justifyContent="flex-end" sx={{ mb: 1 }}>
          <TextField
            size="small"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(0);
            }}
            placeholder={t('pages.single_business.table.search_user', { defaultValue: 'Search users…' })}
            sx={{ minWidth: 240 }}
          />
        </Stack>

        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t('pages.single_business.table.name', { defaultValue: 'Name' })}</TableCell>
                <TableCell>{t('pages.single_business.table.role', { defaultValue: 'Role' })}</TableCell>
                <TableCell>{t('pages.single_business.table.kyc_status', { defaultValue: 'KYC' })}</TableCell>
                <TableCell>{t('pages.single_business.table.status', { defaultValue: 'Status' })}</TableCell>
                <TableCell align="center">
                  {t('pages.manage_businesses.actions', { defaultValue: 'Actions' })}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {pagedMembers.map((member) => {
                const memberName = [member.firstname, member.lastname]
                  .filter(Boolean)
                  .join(' ');
                const displayName =
                  memberName.length > 20 ? `${memberName.slice(0, 20)}…` : memberName;
                return (
                  <TableRow key={member.userId} hover>
                    <TableCell>
                      <Stack direction="row" spacing={1.5} alignItems="center">
                        <Avatar src={member.picture} alt={memberName} sx={{ width: 32, height: 32 }} />
                        <Box>
                          {memberName ? (
                            <Tooltip title={memberName.length > 20 ? memberName : ''}>
                              <Typography variant="body2" fontWeight={500}>
                                {displayName}
                              </Typography>
                            </Tooltip>
                          ) : null}
                          <Typography variant="caption" color="text.secondary">
                            {member.email}
                          </Typography>
                        </Box>
                      </Stack>
                    </TableCell>
                    <TableCell>
                      {ROLE_KEYS[member.profile]
                        ? t(ROLE_KEYS[member.profile], { defaultValue: member.profile })
                        : member.profile}
                    </TableCell>
                    <TableCell>
                      {member.blocked ? (
                        <Chip
                          size="small"
                          label={t('pages.single_business.user_status.blocked', { defaultValue: 'Blocked' })}
                          color="error"
                        />
                      ) : !member.validated ? (
                        <Chip
                          size="small"
                          label={t('pages.single_business.user_status.pending', { defaultValue: 'Pending' })}
                          color="warning"
                        />
                      ) : (
                        <Chip
                          size="small"
                          label={t('pages.single_business.user_status.active', { defaultValue: 'Active' })}
                          color="success"
                        />
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={t(
                          `pages.manage_businesses.be_status.${member.isFreezed ? 'freezed' : 'active'}`,
                          { defaultValue: member.isFreezed ? 'Freezed' : 'Active' },
                        )}
                        color={member.isFreezed ? 'error' : 'success'}
                      />
                    </TableCell>
                    <TableCell align="center">
                      {member.isFreezed ? (
                        <Button
                          size="small"
                          variant="outlined"
                          color="success"
                          onClick={() => { void handleToggleUser(member, false); }}
                        >
                          {t('global.buttons.restore', { defaultValue: 'Restore' })}
                        </Button>
                      ) : (
                        <Button
                          size="small"
                          variant="outlined"
                          color="error"
                          onClick={() => { void handleToggleUser(member, true); }}
                        >
                          {t('global.buttons.block', { defaultValue: 'Block' })}
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
              {pagedMembers.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
                      {t('messages.no-data', { defaultValue: 'No data' })}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={filteredMembers.length}
          page={page}
          onPageChange={(_, p) => setPage(p)}
          rowsPerPage={rowsPerPage}
          rowsPerPageOptions={[rowsPerPage]}
          labelDisplayedRows={({ from, to, count }) =>
            t('pages.userOperations.table.pagination.range', {
              defaultValue: `${from}–${to} of ${count}`,
              from,
              to,
              count,
            })
          }
        />

      </Paper>
    </Box>
  );
}
